import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Tabs,
  Tab,
  Chip,
  Grid,
  CircularProgress
} from '@mui/material';
import { format } from 'date-fns';
import { fetchInvoiceByInvoiceNumber } from 'utils/apiHelper';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const InvoiceHistoryModal = ({ open, onClose, invoiceNumber, variant = 'lab' }) => {
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const fetchHistoryData = async () => {
    if (!invoiceNumber) return;
    
    setLoading(true);
    try {
      const response = await fetchInvoiceByInvoiceNumber(invoiceNumber);
      const invoiceData = Array.isArray(response.results) ? response.results[0] : response;
      setHistoryData(invoiceData);
    } catch (error) {
      console.error('Error fetching invoice history:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && invoiceNumber) {
      fetchHistoryData();
    }
  }, [open, invoiceNumber]);

  const processHistoryData = (data) => {
    if (!data?.items) return { activeItems: [], cancelledItems: [], itemsMap: {} };

    const itemsKey = variant === 'lab' ? 'lab_tests' : 'pharmacy';
    const allItems = data.items[itemsKey] || [];
    
    // Group items by ID to calculate remaining quantities
    const itemsMap = {};
    allItems.forEach(item => {
      const itemId = item.id;
      if (!itemsMap[itemId]) {
        itemsMap[itemId] = {
          id: itemId,
          name: item.name,
          selling_price: item.selling_price || item.price,
          originalQuantity: 0,
          cancelledQuantity: 0,
          remainingQuantity: 0,
          status: 'active',
          created_at: item.created_at,
          created_by: data?.receptionist?.full_name,
          discount_in_perc: item.discount_in_perc || 0,
          batch_number: item.batch_number,
          expiry_date: item.expiry_date,
          manufacturing_date: item.manufacturing_date
        };
      }

      if (item.status === 'active') {
        itemsMap[itemId].originalQuantity += Number(item.quantity);
        itemsMap[itemId].remainingQuantity += Number(item.quantity);
      } else if (item.status === 'cancelled') {
        itemsMap[itemId].cancelledQuantity += Number(item.quantity);
        itemsMap[itemId].remainingQuantity -= Number(item.quantity);
      }
    });

    // Determine final status for each item
    Object.values(itemsMap).forEach(item => {
      if (item.remainingQuantity <= 0) {
        item.status = 'fully_cancelled';
      } else if (item.cancelledQuantity > 0) {
        item.status = 'partially_cancelled';
      } else {
        item.status = 'active';
      }
    });

    const activeItems = Object.values(itemsMap).filter(item => item.remainingQuantity > 0);
    const cancelledItems = allItems.filter(item => item.status === 'cancelled');

    return { activeItems, cancelledItems, itemsMap };
  };

  const renderActiveItemsTable = (activeItems) => (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>{variant === 'lab' ? 'Lab Test' : 'Medicine'}</TableCell>
            <TableCell align="center" sx={{ justifyItems: 'center' }}>Original Qty</TableCell>
            <TableCell align="center" sx={{ justifyItems: 'center' }}>Cancelled Qty</TableCell>
            <TableCell align="center" sx={{ justifyItems: 'center' }}>Remaining Qty</TableCell>
            <TableCell align="right">Amount (₹)</TableCell>
            <TableCell align="center">Status</TableCell>
            <TableCell>Created At</TableCell> 
          </TableRow>
        </TableHead>
        <TableBody>
          {activeItems.map((item, index) => (
                         <TableRow key={item.id}>
               <TableCell>{index + 1}</TableCell>
               <TableCell>{item.name}</TableCell>
               <TableCell align="center" sx={{ justifyItems: 'center' }}>{item.originalQuantity}</TableCell>
               <TableCell align="center" sx={{ justifyItems: 'center' }}>{item.cancelledQuantity}</TableCell>
               <TableCell align="center" sx={{ justifyItems: 'center' }}>{item.remainingQuantity}</TableCell>
               <TableCell align="right">{(Number(item.selling_price) * Number(item.quantity) * (1 - Number(item.discount_in_perc)/100)).toFixed(2)}</TableCell>
               <TableCell align="center" sx={{ justifyItems: 'center' }}>
                 <Chip
                   label={item.status === 'partially_cancelled' ? 'Partially Cancelled' : 'Active'}
                   color={item.status === 'partially_cancelled' ? 'warning' : 'success'}
                   size="small"
                 />
               </TableCell>
               <TableCell>
                 {item.created_at ? format(new Date(item.created_at), 'dd/MM/yyyy HH:mm') : '-'}
               </TableCell>
             </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderCancelledItemsTable = (cancelledItems) => (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
                     <TableRow>
             <TableCell>#</TableCell>
             <TableCell>{variant === 'lab' ? 'Lab Test' : 'Medicine'}</TableCell>
             <TableCell align="center" sx={{ justifyItems: 'center' }}>Cancelled Qty</TableCell>
             <TableCell align="right">Amount (₹)</TableCell>
             <TableCell align="center">Status</TableCell>
             <TableCell>Created At</TableCell>
           </TableRow>
        </TableHead>
        <TableBody>
          {cancelledItems.map((item, index) => (
                         <TableRow key={`${item.id}-${index}`}>
               <TableCell>{index + 1}</TableCell>
               <TableCell>{item.name}</TableCell>
               <TableCell align="center" sx={{ justifyItems: 'center' }}>{item.quantity}</TableCell>
               <TableCell align="right">{(Number(item.selling_price) * Number(item.quantity) * (1 - Number(item.discount_in_perc)/100)).toFixed(2)}</TableCell>
               <TableCell align="center" sx={{ justifyItems: 'center' }}>
                 <Chip label="Cancelled" color="error" size="small" />
               </TableCell>
               <TableCell>
                 {item.created_at ? format(new Date(item.created_at), 'dd/MM/yyyy HH:mm') : '-'}
               </TableCell>
             </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (!historyData && !loading) {
    return null;
  }

  const { activeItems, cancelledItems } = processHistoryData(historyData);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h6">
          Invoice History - {invoiceNumber}
        </Typography>
        {historyData && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="textSecondary">
                Patient: {historyData.customer?.full_name || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="textSecondary">
                Created: {historyData.created_at ? format(new Date(historyData.created_at), 'dd/MM/yyyy HH:mm') : 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        )}
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tab label={`Active Items (${activeItems.length})`} />
              <Tab label={`Cancelled Items (${cancelledItems.length})`} />
            </Tabs>
            
            <TabPanel value={tabValue} index={0}>
              {activeItems.length > 0 ? (
                renderActiveItemsTable(activeItems)
              ) : (
                <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 4 }}>
                  No active items found
                </Typography>
              )}
            </TabPanel>
            
            <TabPanel value={tabValue} index={1}>
              {cancelledItems.length > 0 ? (
                renderCancelledItemsTable(cancelledItems)
              ) : (
                <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 4 }}>
                  No cancelled items found
                </Typography>
              )}
            </TabPanel>
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

InvoiceHistoryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  invoiceNumber: PropTypes.string,
  variant: PropTypes.oneOf(['lab', 'pharmacy']).isRequired
};

export default InvoiceHistoryModal; 